import type { IIndustryCodesFilterDto } from "app/pages/sales-intelligence/domain/industry/types/filter-values";
import type {
    IAdSpendChannelsFilterDto,
    IAdSpendVolumeFilterDto,
    IDisplayAdsFormatFilterDto,
    IEngagementFilterDto,
    IIncomingTrafficFilterDto,
    IInternationalVisitsFilterDto,
    IMarketingChannelsFilterDto,
} from "app/pages/sales-intelligence/domain/leads/types/filter-values";
import type { TechnologiesFilterDto } from "app/pages/sales-intelligence/domain/technology/types/filter-values";
import type { GroupedTechnologiesCategoryMap } from "app/pages/sales-intelligence/domain/technology/types/model";
import type { LeadsSearchRowKeys } from "app/pages/sales-intelligence/modules/leads-search";

import type { FilterRulesEngine } from "../types/rules";
import processAdSpendChannelsFilter from "../utils/ad-spend-channels-filter/processAdSpendChannelsFilter";
import processAdSpendVolumeFilter from "../utils/ad-spend-volume-filter/processAdSpendVolumeFilter";
import processDisplayAdsFormatFilter from "../utils/display-ads-format-filter/processDisplayAdsFormatFilter";
import processEngagementFilter from "../utils/engagement-filter/processEngagementFilter";
import processIncomingTrafficFilter from "../utils/incoming-traffic-filter/processIncomingTrafficFilter";
import processIndustryCodesFilter from "../utils/industry-filter/processIndustryCodesFilter";
import { processInternationalFilters } from "../utils/international-filters/processInternationalFilters";
import processMarketingChannelsFilter from "../utils/marketing-channels/processMarketingChannelsFilter";
import { processTechnologiesFilter } from "../utils/technologies-filter/processTechnologiesFilter";

type Deps = {
    groupedTechnologies: GroupedTechnologiesCategoryMap;
};

export const createColumnByFilterRulesEngine = (deps: Deps): FilterRulesEngine => {
    return {
        industryFilter: () => ["industry"],

        industryCodesFilter: (filterDto: readonly IIndustryCodesFilterDto[]) => processIndustryCodesFilter(filterDto),

        industryTaxonomy: () => ["industry_taxonomy"],

        siteTags: () => ["site_tags"],

        technologies: (technologiesFilterDto: TechnologiesFilterDto[]) =>
            processTechnologiesFilter(technologiesFilterDto, deps.groupedTechnologies),

        onlineRevenue: () => ["company_online_revenue_range"],

        monthlyAverageTransactions: () => ["monthly_avg_transactions_range"],

        companyHeadquarter: () => [
            "company_city",
            "company_state",
            "company_country",
            "company_address",
            "company_zip",
        ],

        employeeHeadcount: () => ["company_employee_range"],

        businessModels: () => ["business_model"],

        leadScores: () => ["lead_priority"],

        annualRevenue: () => ["company_revenue_range"],

        conversionRate: () => ["conversion_rate", "conversion_rate_change_mom", "conversion_rate_change_yoy"],

        topGeoCountries: () => ["top_geo_country"],

        internationalFilters: (filterDto: IInternationalVisitsFilterDto) => processInternationalFilters(filterDto),

        incomingTraffic: (trafficfilterDto: Partial<IIncomingTrafficFilterDto>) =>
            processIncomingTrafficFilter(trafficfilterDto),

        engagement: (filterDto: Partial<IEngagementFilterDto>) => processEngagementFilter(filterDto),

        marketingChannels: (filterDto: Partial<IMarketingChannelsFilterDto>) =>
            processMarketingChannelsFilter(filterDto),

        adSpendVolume: (filterDto: Partial<IAdSpendVolumeFilterDto>) => processAdSpendVolumeFilter(filterDto),

        adSpendChannels: (filterDto: Partial<IAdSpendChannelsFilterDto>) => processAdSpendChannelsFilter(filterDto),

        displayAdsFormat: (filterDto: Partial<IDisplayAdsFormatFilterDto>) => processDisplayAdsFormatFilter(filterDto),

        usdSpend: () => ["usd_spend"],

        audienceGenderSplits: () => ["male_vs_female_share"],

        audienceAgeGroups: (filterDto: readonly LeadsSearchRowKeys[]) => filterDto,

        signalsEcommerce: () => ["ecommerce_signal_data"],
    };
};
